import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { waLink, WA_MESSAGES } from "./data";

const LINKS = [
  { label: "Manifiesto", href: "#manifiesto" },
  { label: "Departamentos", href: "#departamentos" },
  { label: "Tour", href: "#tour" },
  { label: "Amenidades", href: "#amenidades" },
  { label: "Ubicación", href: "#ubicacion" },
];

export const scrollTo = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 2.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#1c242f]/85 backdrop-blur-xl border-b border-white/5" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <button
            data-testid="nav-logo-button"
            onClick={() => scrollTo("#inicio")}
            className="flex items-center gap-3"
          >
            <img src="/images/logo_horizontal.webp" alt="Condominio EHÉCATL" className="h-9 w-auto" />
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                onClick={() => scrollTo(l.href)}
                className="link-line text-[13px] tracking-wide text-[#a8ac9b] hover:text-[#f4f2ea] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="nav-whatsapp-cta"
              href={waLink(WA_MESSAGES.visita)}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#bfa360] px-5 py-2.5 text-[13px] font-semibold text-[#1c242f] hover:bg-[#ddc68c] transition-colors"
            >
              <MessageCircle size={15} />
              Agenda tu visita
            </a>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-[#f4f2ea]"
              aria-label="Menú"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#1c242f]/97 backdrop-blur-2xl lg:hidden flex flex-col items-center justify-center gap-8"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i }}
                onClick={() => { setOpen(false); scrollTo(l.href); }}
                className="font-display text-4xl text-[#f4f2ea]"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.a
              data-testid="nav-mobile-whatsapp-cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              href={waLink(WA_MESSAGES.visita)}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#bfa360] px-8 py-4 font-semibold text-[#1c242f]"
            >
              <MessageCircle size={18} />
              Agenda tu visita
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
